import { memo, type MouseEvent, type ReactNode, useMemo } from 'react'
import { CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { DropdownMenu } from '@skop-app/lib-front'
import type { ICellRendererParams, IRowNode } from 'ag-grid-community'
import { cx } from 'class-variance-authority'

import {
  buildInventoryStatusCatalog,
  EMPTY_INVENTORY_STATUS_CATALOG,
  getInventoryQuickStatusActions,
} from '@/features/inventory/lib/inventory.statusActions'
import type { SelectOption } from '@/services/property/option.normalized'
import type { GridContext } from '@/types/grid'
import { ProductStatus } from '@skop-app/lib-types'

type RowWithAction = ProductStatus

type ActionOption = Pick<
  SelectOption,
  'id' | 'label' | 'value' | 'icon' | 'options'
>

const resolveOption = (value: string, options: ActionOption[]) => {
  const stack: ActionOption[] = [...options]

  while (stack.length > 0) {
    const option = stack.pop()
    if (!option) continue

    if (
      option.value === value ||
      option.id === value ||
      option.label?.toLowerCase() === value.toLowerCase()
    ) {
      return { icon: option.icon, label: option.label }
    }

    for (const child of option.options ?? []) {
      stack.push(child)
    }
  }

  return { icon: undefined, label: value }
}

const getActionFromNode = (
  node: IRowNode | null | undefined,
): string | null => {
  const data = node?.data as RowWithAction | null | undefined
  const action = data?.action
  return typeof action === 'string' ? action : null
}

const getVisibleGroupActions = (
  node: IRowNode | null | undefined,
): string[] => {
  if (!node) return []

  if (!node.group) {
    const action = getActionFromNode(node)
    return action ? [action] : []
  }

  const children = Array.isArray(node.childrenAfterFilter)
    ? (node.childrenAfterFilter as IRowNode[])
    : Array.isArray(node.allLeafChildren)
      ? (node.allLeafChildren as IRowNode[])
      : []

  return children.flatMap((child) => getVisibleGroupActions(child))
}

const preventGridFocus = (event: MouseEvent) => {
  event.preventDefault()
  event.stopPropagation()
}

const renderStatusIcon = (icon: string | undefined, label: string) =>
  icon ? <img src={`/icon/${icon}.svg`} className="size-5" alt={label} /> : null

const StatusChip = ({
  label,
  icon,
  isChild,
  withTrigger,
}: {
  label: string
  icon?: string
  isChild: boolean
  withTrigger: boolean
}) => (
  <div
    className={cx(
      'flex h-full items-center gap-2 rounded-sm border border-orange-300 bg-orange-200 px-2',
      {
        'bg-primary-50': isChild,
        'cursor-pointer pr-1': withTrigger,
      },
    )}
  >
    {renderStatusIcon(icon, label)}
    <span className="text-xs">{label}</span>
    {withTrigger && <CaretDownIcon className="ml-auto size-3.5 shrink-0" />}
  </div>
)

const renderLeaflessValue = (
  value: unknown,
  options: ActionOption[],
): ReactNode => {
  if (typeof value !== 'string') return null
  const { icon, label } = resolveOption(value, options)

  return (
    <div className="flex items-center gap-2 p-1">
      {renderStatusIcon(icon, label)}
      <span className="text-xs">{label}</span>
    </div>
  )
}

export const ActionStatusCellRenderer = memo(
  (props: ICellRendererParams<RowWithAction, unknown>) => {
    const { data, node, value, context, eGridCell } = props
    const { offsetWidth, offsetHeight } = eGridCell || {}

    const gridContext = (context ?? {}) as GridContext
    const rawActionOptions = Array.isArray(gridContext.action)
      ? (gridContext.action as SelectOption[])
      : []
    const options = rawActionOptions as ActionOption[]
    const actionCatalog = useMemo(
      () =>
        gridContext.actionCatalog ??
        (rawActionOptions.length > 0
          ? buildInventoryStatusCatalog(rawActionOptions)
          : EMPTY_INVENTORY_STATUS_CATALOG),
      [gridContext.actionCatalog, rawActionOptions],
    )

    if (!node && value !== undefined) {
      return renderLeaflessValue(value, options)
    }

    if (
      (!data && !node?.group) ||
      (typeof data?.id === 'string' && data.id.includes('new'))
    ) {
      return null
    }

    const quickStatusActions = getInventoryQuickStatusActions(actionCatalog)
    const canQuickUpdate =
      Boolean(node) &&
      typeof gridContext.runInventoryQuickStatusAction === 'function' &&
      quickStatusActions.length > 0

    const groupActions = getVisibleGroupActions(node)
    const actionCounts = groupActions.reduce<Record<string, number>>(
      (acc, action) => {
        acc[action] = (acc[action] ?? 0) + 1
        return acc
      },
      {},
    )
    const uniqueActions = Object.entries(actionCounts)

    const content =
      node?.group && uniqueActions.length > 1 ? (
        <div
          className="flex h-full items-center gap-1 p-1"
          style={{ width: `${offsetWidth}px`, height: `${offsetHeight}px` }}
        >
          {uniqueActions.map(([action, count]) => {
            const { icon, label } = resolveOption(action, options)

            return (
              <div
                key={action}
                className={cx(
                  'flex h-full items-center rounded-sm border border-orange-300 bg-orange-200 px-2',
                  {
                    'cursor-pointer': canQuickUpdate,
                    'w-full': uniqueActions.length === 1,
                  },
                )}
              >
                <div className="flex items-center gap-2">
                  {renderStatusIcon(icon, label)}
                  {uniqueActions.length === 1 && (
                    <span className="text-xs">{label}</span>
                  )}
                </div>
                <span className="ml-1 text-xs">({count})</span>
                {canQuickUpdate && (
                  <CaretDownIcon className="ml-2 size-3.5 shrink-0" />
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div
          style={{ width: `${offsetWidth}px`, height: `${offsetHeight}px` }}
          className="p-1"
        >
          <StatusChip
            label={(() => {
              const rowActionRaw = (data as RowWithAction | undefined)?.action
              const action = node?.group
                ? (uniqueActions[0]?.[0] ?? 'diagnostic')
                : typeof rowActionRaw === 'string'
                  ? rowActionRaw
                  : typeof value === 'string'
                    ? value
                    : 'diagnostic'
              return resolveOption(action, options).label
            })()}
            icon={(() => {
              const rowActionRaw = (data as RowWithAction | undefined)?.action
              const action = node?.group
                ? (uniqueActions[0]?.[0] ?? 'diagnostic')
                : typeof rowActionRaw === 'string'
                  ? rowActionRaw
                  : typeof value === 'string'
                    ? value
                    : 'diagnostic'
              return resolveOption(action, options).icon
            })()}
            isChild={(node?.level ?? 0) > 0}
            withTrigger={canQuickUpdate}
          />
        </div>
      )

    if (
      !canQuickUpdate ||
      !node ||
      !gridContext.runInventoryQuickStatusAction
    ) {
      return content
    }

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="block h-full w-full text-left"
            onMouseDown={preventGridFocus}
            onClick={preventGridFocus}
          >
            {content}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start" className="w-56 bg-white">
          {quickStatusActions.map((quickAction) => (
            <DropdownMenu.Item
              key={quickAction.key}
              onClick={() =>
                gridContext.runInventoryQuickStatusAction?.({
                  node: node as IRowNode<Record<string, unknown>>,
                  payload: quickAction.payload,
                })
              }
            >
              <div className="flex items-center gap-2">
                {quickAction.icon ? (
                  <img
                    src={`/icon/${quickAction.icon}.svg`}
                    alt=""
                    className="size-4"
                  />
                ) : null}
                <span>{quickAction.label}</span>
              </div>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    )
  },
)

ActionStatusCellRenderer.displayName = 'ActionStatusCellRenderer'
